'use client';
import { PlayIcon } from '@/components/icons/play';
import { QuoteIcon } from '@/components/icons/quote';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselApi,
} from '@/components/ui/carousel';
import { H2 } from '@/components/ui/typography';
import { Star } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useState } from 'react';

export type Testimonial = {
  name: string;
  country: string;
  image: string;
  message: string;
};

export const testimonials: Testimonial[] = [
  {
    name: 'Jang Wonyoung',
    country: 'Korea',
    image: 'https://randomuser.me/api/portraits/women/65.jpg',
    message:
      'We value your feedback and are committed to providing exceptional service to all our clients. If you have any concerns or questions...',
  },
  {
    name: 'Jennie Kim',
    country: 'USA',
    image: 'https://randomuser.me/api/portraits/women/66.jpg',
    message:
      'Our priority, and we appreciate the opportunity to address any issues and ensure your complete satisfaction.',
  },
  {
    name: 'Sophia Lee',
    country: 'Canada',
    image: 'https://randomuser.me/api/portraits/women/67.jpg',
    message:
      'Exceptional team! They responded quickly to our needs and delivered beyond expectations.',
  },
  {
    name: 'Rahul Sharma',
    country: 'India',
    image: 'https://randomuser.me/api/portraits/men/68.jpg',
    message:
      'The experience was smooth, professional, and efficient. Highly recommended.',
  },
  {
    name: 'Liam Wilson',
    country: 'Australia',
    image: 'https://randomuser.me/api/portraits/men/69.jpg',
    message:
      'I appreciate their transparency and the constant updates throughout the process.',
  },
  {
    name: 'Amina Yusuf',
    country: 'UAE',
    image: 'https://randomuser.me/api/portraits/women/70.jpg',
    message:
      "They exceeded all expectations. We're very happy with the service quality.",
  },
  {
    name: 'Carlos Martinez',
    country: 'Mexico',
    image: 'https://randomuser.me/api/portraits/men/71.jpg',
    message:
      'I was impressed with the attention to detail and dedication to customer satisfaction.',
  },
];

export const TestimonialSection = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return (
    <section className="py-8 sm:py-12 lg:py-16 xl:py-20 px-4 sm:px-5 md:px-6 lg:px-8 w-full">
      <div className="max-w-[1252px] w-full mx-auto">
        <H2 className="text-center font-dmSans font-semibold mb-8 sm:mb-10 lg:mb-[60px]">
          What Our Users Say
        </H2>

        {/* Mobile Layout (sm and below) */}
        <div className="block lg:hidden space-y-6 sm:space-y-8">
          {/* Hero Image */}
          <div className="w-full aspect-[4/3] sm:aspect-[3/2] rounded-2xl sm:rounded-[24px] overflow-hidden relative">
            <div className="absolute left-4 sm:left-6 bottom-4 sm:bottom-6">
              <PlayIcon />
            </div>
            <Image
              src="/images/girl_1.png"
              alt="Girl"
              width={100}
              height={100}
              className="size-full object-cover"
            />
          </div>

          {/* Title */}
          <div className="text-center px-2">
            <h3 className="text-2xl sm:text-3xl font-dmSans font-medium text-[#353232] leading-tight">
              <span className="gradient-text">500+</span> Happy Clients Said To
              Us They Are Satisfied
            </h3>
          </div>

          {/* Testimonials Carousel */}
          <div className="w-full">
            <Carousel
              setApi={setApi}
              className="w-full"
              opts={{
                loop: true,
                align: 'start',
              }}
            >
              <CarouselContent className="h-full">
                {testimonials.map((item, index) => (
                  <CarouselItem
                    key={index}
                    className="basis-full sm:basis-4/5 h-full"
                  >
                    <div className="size-full rounded-2xl sm:rounded-[24px] bg-white border border-[#e6e6e6]">
                      <div className="pt-5 sm:pt-[25px] px-4 sm:px-[23px] pb-6 sm:pb-10 space-y-4 sm:space-y-6">
                        <QuoteIcon />
                        <p className="text-sm sm:text-base font-dmSans text-[#838383] font-medium">
                          {item.message}
                        </p>
                      </div>
                      <div className="px-4 sm:px-7 pb-6 sm:pb-8">
                        <div className="w-full border-t border-[#ededed]">
                          <div className="pt-4 sm:pt-6 flex items-center gap-3 sm:gap-[22px]">
                            <Image
                              src={item.image}
                              alt={item.name}
                              width={100}
                              height={100}
                              className="size-10 sm:size-12 rounded-full object-cover"
                            />
                            <div>
                              <p className="text-sm sm:text-base font-dmSans text-[#151313] font-bold">
                                {item.name}
                              </p>
                              <p className="text-sm sm:text-base font-dmSans text-[#838383]">
                                {item.country}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        </div>

        {/* Desktop Layout (lg and above) */}
        <div className="hidden lg:block">
          <div className="grid grid-cols-[320px_130px_1fr] xl:grid-cols-[323px_133px_1fr] mt-[60px] mb-10 grid-rows-[160px_auto_48px] xl:grid-rows-[164px_auto_48px] gap-0 w-full">
            <div className="row-start-1 row-end-3 col-start-1 col-end-3">
              <div className="w-full rounded-[24px] h-[400px] xl:h-[412px] overflow-hidden relative">
                <div className="absolute left-6 bottom-6">
                  <PlayIcon />
                </div>
                <Image
                  src="/images/girl_1.png"
                  alt="Girl"
                  width={100}
                  height={100}
                  className="size-full object-cover"
                />
              </div>
            </div>

            <div className="row-start-1 row-end-2 col-start-3 col-end-4 px-6 xl:px-7 pt-3">
              <h3 className="text-3xl xl:text-[45px] font-dmSans font-medium text-[#353232] leading-tight xl:leading-[60px]">
                <span className="gradient-text">500+</span> Happy Clients Said
                To Us They Are Satisfied
              </h3>
            </div>

            <div className="row-start-2 row-end-4 col-start-2 col-end-4">
              <Carousel
                setApi={setApi}
                className="size-full"
                opts={{
                  loop: true,
                  align: 'start',
                }}
              >
                <CarouselContent className="h-full">
                  {testimonials.map((item, index) => (
                    <CarouselItem key={index} className="basis-1/2 h-full">
                      <div className="size-full rounded-[24px] bg-white border border-[#e6e6e6]">
                        <div className="pt-[25px] px-[23px] pb-10 space-y-6">
                          <QuoteIcon />
                          <p className="text-base font-dmSans text-[#838383] font-medium">
                            {item.message}
                          </p>
                        </div>
                        <div className="px-7 pb-8">
                          <div className="w-full border-t border-[#ededed]">
                            <div className="pt-6 flex items-center gap-[22px]">
                              <Image
                                src={item.image}
                                alt={item.name}
                                width={100}
                                height={100}
                                className="size-12 rounded-full object-cover"
                              />
                              <div>
                                <p className="text-base font-dmSans text-[#151313] font-bold">
                                  {item.name}
                                </p>
                                <p className="text-base font-dmSans text-[#838383]">
                                  {item.country}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
          </div>
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center items-center gap-1.5 mt-6 sm:mt-8 lg:mt-0">
          {Array.from({ length: count }).map((_, index) => (
            <button
              key={index}
              className={`size-2.5 rounded-full transition-all duration-300 ${
                index + 1 === current
                  ? 'bg-primary-gradient'
                  : 'bg-[#d9d9d9] hover:bg-[#d0d0d0]'
              }`}
              onClick={() => api?.scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
